
import { useBookableResourcesQuery, useEventTypesQuery } from '~/lib/Queries';
import { getBookWithLabel } from '~/lib/BookableResourceHelpers';
import CheckboxList from '~/components/CheckboxList';
import { useEffect, useMemo, useRef, useState } from 'react';
import useEventColors from '~/lib/useEventColors';
import DotDotDotMenu from '~/components/DotDotDotMenu';
import ColorPickerGrid from '~/components/ColorPickerGrid';
import SectionPicker from '~/components/SectionPicker';
import { isMobile } from '~/lib/Utils.js';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { getBusinessId } from '~/lib/Api';

export function filterEvents(filter, events, colorBasedOn) {
    if (!Array.isArray(filter)) {
        return events;
    }

    if (colorBasedOn === 'eventType') {
        return events.filter(ev => {
            const eventTypeId = ev.event_type?.id;
            if (!eventTypeId) {
                // Always show for things like blocked off times
                return true;
            }
            return filter.indexOf(eventTypeId) > -1;
        });
    }

    // Default to resource filtering
    return events.filter(ev => {
        const ids = ev.all_bookable_resource_ids || [ev.bookable_resource?.id];
        for (let resourceId of ids) {
            if (filter.indexOf(resourceId) > -1) {
                return true;
            }
        }
        return false;
    });
}

const ColorPickerMenu = ({ colors, setColors, id, preview }) => {
    const selectedColor = (colors && colors[id]) || '#2C87F3';
    return (
        <DotDotDotMenu
            menuItemsClassName="min-w-[300px] "
            btnContent={
                !!preview ? (
                    <div
                        className={`w-4 h-4 rounded-full bg-primary-700`}
                        style={{ backgroundColor: selectedColor }}
                    />
                ) : undefined
            }
            options={[
                {
                    name: 'Color',
                    onClick: () => true,
                    description: (
                        <ColorPickerGrid
                            compact={true}
                            value={selectedColor}
                            onChange={newValue =>
                                setColors({
                                    ...colors,
                                    [id]: newValue,
                                })
                            }
                        />
                    ),
                },
            ]}
        />
    );
};

const CalendarFilterBookableResources = ({ className, onFilterChange, onResourceFilterChange }) => {
    const { data: bookableResourceData, isLoading } =
        useBookableResourcesQuery(true);

    const { data: eventTypesData } = useEventTypesQuery();
    const [showOptionsSetting, setShowOptions] = useState(false);

    const {
        resourceColors,
        setResourceColors,
        typeColors,
        setTypeColors,
        colorBasedOn,
        setColorBasedOn,
    } = useEventColors();
    const resources = bookableResourceData || [];
    const eventTypes = eventTypesData || [];

    // Separate localStorage keys for resource and event type filters
    const resourceFilterKey = 'calendar-resource-filter-' + getBusinessId();
    const eventTypeFilterKey = 'calendar-eventtype-filter-' + getBusinessId();

    // Load initial filter from localStorage based on colorBasedOn
    const getInitialFilter = () => {
        if (colorBasedOn === 'resource') {
            const stored = localStorage.getItem(resourceFilterKey);
            return stored ? JSON.parse(stored) : null;
        } else {
            const stored = localStorage.getItem(eventTypeFilterKey);
            return stored ? JSON.parse(stored) : null;
        }
    };

    const [resourceFilter, setResourceFilter] = useState(() => {
        const stored = localStorage.getItem(resourceFilterKey);
        return stored ? JSON.parse(stored) : null;
    });

    const [eventTypeFilter, setEventTypeFilter] = useState(() => {
        const stored = localStorage.getItem(eventTypeFilterKey);
        return stored ? JSON.parse(stored) : null;
    });

    // Get the current filter based on colorBasedOn
    const currentFilter = colorBasedOn === 'resource' ? resourceFilter : eventTypeFilter;

    const resourceOptions = useMemo(() => {
        return resources.map(br => ({
            value: br.id,
            label: br.name,
        }));
    }, [resources]);

    const eventTypeOptions = useMemo(() => {
        return eventTypes.map(et => ({
            value: et.id,
            label: et.name,
        }));
    }, [eventTypes]);

    // Initialize filters with all items selected if nothing is stored
    useEffect(() => {
        if (colorBasedOn === 'resource' && resources?.length > 0) {
            if (resourceFilter === null) {
                const allResourceIds = resources.map(br => br.id);
                setResourceFilter(allResourceIds);
                localStorage.setItem(resourceFilterKey, JSON.stringify(allResourceIds));
            }
        } else if (colorBasedOn === 'eventType' && eventTypes?.length > 0) {
            if (eventTypeFilter === null) {
                const allEventTypeIds = eventTypes.map(et => et.id);
                setEventTypeFilter(allEventTypeIds);
                localStorage.setItem(eventTypeFilterKey, JSON.stringify(allEventTypeIds));
            }
        }
    }, [colorBasedOn, resources, eventTypes, resourceFilter, eventTypeFilter]);

    // Update localStorage when filters change
    useEffect(() => {
        if (resourceFilter !== null) {
            localStorage.setItem(resourceFilterKey, JSON.stringify(resourceFilter));
        }

        if (onResourceFilterChange) {
            onResourceFilterChange(resourceFilter);
        }
    }, [resourceFilter, resourceFilterKey]);

    useEffect(() => {
        if (eventTypeFilter !== null) {
            localStorage.setItem(eventTypeFilterKey, JSON.stringify(eventTypeFilter));
        }
    }, [eventTypeFilter, eventTypeFilterKey]);

    // Notify parent component when filter changes
    useEffect(() => {
        if (currentFilter !== null && onFilterChange) {
            onFilterChange(currentFilter);
        }
    }, [currentFilter, onFilterChange]);

    // Handler to update the appropriate filter
    const handleFilterChange = (newFilter) => {
        if (colorBasedOn === 'resource') {
            setResourceFilter(newFilter);
        } else {
            setEventTypeFilter(newFilter);
        }
    };

    const maxOptions = 5;
    const showOptions =
        showOptionsSetting ||
        !isMobile() ||
        (colorBasedOn === 'eventType' && eventTypes?.length < maxOptions) ||
        (colorBasedOn === 'resource' && resourceOptions?.length < maxOptions);

    return (
        <div className={className}>
            <div className="panel-label">Color Calendar By</div>
            <SectionPicker
                value={colorBasedOn}
                onChange={value => {
                    setColorBasedOn(value);
                    setShowOptions(true);
                }}
                slim={true}
                className="mb-2"
                options={[
                    {
                        value: 'resource',
                        label: getBookWithLabel(resources, 'Resource') || 'Person',
                        //label: getBookWithLabel(resources, 'Resource'),
                    },
                    { value: 'eventType',
                      label: 'Event Type'
                    },
                ]}
            />
            {colorBasedOn === 'resource' && showOptions && (
                <div>
                    <div className="panel-label">
                        {/*{getBookWithLabel(resources, 'Show')}*/}
                        {!currentFilter?.length && (
                            <span className="ml-4 text-red-600">
                                Nothing selected
                            </span>
                        )}
                    </div>
                    <CheckboxList
                        value={currentFilter}
                        onChange={handleFilterChange}
                        options={resourceOptions}
                        className="panel pl-4"
                        containerClassName="divide-y"
                        renderCheckbox={({ opt, isChecked, toggle }) => (
                            <input
                                type="checkbox"
                                value={opt.value}
                                checked={isChecked}
                                className="mr-2 mt-1"
                                style={{
                                    backgroundColor:
                                        resourceColors &&
                                        resourceColors[opt.value],
                                }}
                                onChange={toggle}
                            />
                        )}
                        renderLabel={({ opt }) => (
                            <div className="flex-1 flex items-center justify-between pr-4">
                                <div>{opt.label}</div>
                                <ColorPickerMenu
                                    colors={resourceColors}
                                    setColors={setResourceColors}
                                    id={opt.value}
                                />
                            </div>
                        )}
                    />
                </div>
            )}
            {colorBasedOn === 'eventType' && showOptions && (
                <div>
                    <div className="panel-label">
                        {!currentFilter?.length && (
                            <span className="ml-4 text-red-600">
                                Nothing selected
                            </span>
                        )}
                    </div>
                    <CheckboxList
                        value={currentFilter}
                        onChange={handleFilterChange}
                        options={eventTypeOptions}
                        className="panel pl-4"
                        containerClassName="divide-y"
                        renderCheckbox={({ opt, isChecked, toggle }) => (
                            <input
                                type="checkbox"
                                value={opt.value}
                                checked={isChecked}
                                className="mr-2 mt-1"
                                style={{
                                    backgroundColor:
                                        typeColors &&
                                        typeColors[opt.value],
                                }}
                                onChange={toggle}
                            />
                        )}
                        renderLabel={({ opt }) => (
                            <div className="flex-1 flex items-center justify-between pr-4">
                                <div>{opt.label}</div>
                                <ColorPickerMenu
                                    colors={typeColors}
                                    setColors={setTypeColors}
                                    id={opt.value}
                                />
                            </div>
                        )}
                    />
                </div>
            )}
            {!showOptions && (
                <button
                    type="button"
                    className="btn-link-sm ml-6"
                    onClick={() => setShowOptions(true)}
                >
                    Show Options{' '}
                    <ChevronDownIcon className="w-4 h-4 inline-block" />
                </button>
            )}
        </div>
    );
};

export default CalendarFilterBookableResources;
